import { redeemPointsToArs } from '../../../loyalty/domain/loyaltyTypes'
import { usePosLoyaltyStore } from '../../../loyalty/store/usePosLoyaltyStore';
import { captureCatch } from '../../../shared/sentry/sentryUser';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { NavigationProp } from '@react-navigation/native';
import { usePosCheckoutStore } from '../../store/usePosCheckoutStore';
import { usePosCashSessionStore } from '../../store/usePosCashSessionStore';
import { derivePosCheckoutTotals } from '../../application/posScreen.utils'
import { listPosCheckoutCustomers } from '../../application/posCheckoutCommands'
import { mapStockWarningsByProductId } from '../../application/posCartStockIssuePresentation'
import type { PosSaleLineInput, PosStockSalePolicy } from '../../domain/posTypes';
import type { PosFiscalDefaultStrategy } from '../../../fiscal/domain-api';
import { usePosCheckoutTransaction } from './usePosCheckoutTransaction';
import { usePosStockWarningsForCheckout } from './usePosStockWarningsForCheckout';
import { usePosDiningCheckout } from './usePosDiningCheckout';
import { usePosMpQrActions } from './usePosMpQrActions';
import { usePosTableSession } from './usePosTableSession';
import { useShallow } from 'zustand/react/shallow';
import { useHasBusinessCapability } from '../../../shell/session/hooks/useEffectiveBusinessCapabilities';
import { upsertTableSessionCart, upsertTableSessionTipIntent } from '../../../hospitality/infra/hospitalityRepo';
import { usePosStore } from '../../store/usePosStore';
import type { CartItem } from '../../store/usePosStore';
import { formatOptionsLabel } from '../../../catalog/domain-api';
import { mapCartLikeToPreCuentaLines } from '../../../hospitality/screens/buildPreCuenta';
import {
  printPreCuentaDocumentSafe,
  sharePreCuentaDocumentSafe,
} from '../../../printing/application/printComandaDocuments';
import { showError, showInfo, showSuccess } from '../../../shared/ui/appToast';
import { derivePosCheckoutMoney } from '../../application/posCheckoutMoney';
import { resolvePosStaffTipAmount, usePosStaffTipsEnabled } from '../../application/posStaffTips';
import { tipAmountFromPercent, type PosTipIntent } from '../../application/posCheckoutTip';
import { formatEditableMoneyAmount } from '../../../shared/numeric/localeNumeric';
import { getPosFulfillmentType } from '../../../shared/pos/posHandoffSettings';

type Nav = NavigationProp<Record<string, object | undefined>>;

type Params = {
  navigation: Nav;
  currentBranchId: string | null | undefined;
  cashSessionReady: boolean;
  stockSalePolicy: PosStockSalePolicy;
  cart: Parameters<typeof derivePosCheckoutTotals>[0];
  cartEvaluation: Parameters<typeof derivePosCheckoutTotals>[2];
  promoUiStatus?: import('../../application/posCartPromoPresentation').CartPromoUiStatus | null;
  saleItems: PosSaleLineInput[];
  discretionaryDiscountTotal?: number;
  appliedPromoCodes?: string[];
  selectedCashierPromotionId?: string | null;
  clearCart: () => void;
  userId?: string | null;
  canOpenCashManagementFromPos: boolean;
  mercadopagoQrMethodAllowed: boolean;
  customerMethodAllowed: boolean;
  checkMethodAllowed: boolean;
  allowFiscalSaleMode: boolean;
  defaultCheckoutFiscalMode: PosFiscalDefaultStrategy;
  hospitalityEnabled: boolean;
  hasKitchenCapability: boolean;
  hasTablesCapability: boolean;
  activeDraftId?: string | null;
  clearPersistenceAfterSale: () => Promise<void>;
  reloadCatalog: () => void | Promise<void>;
  setMobileCartSheetVisible: (visible: boolean) => void;
  initialDiningTableId?: string | null;
  tableOpenKey?: string | null;
  tablesideCheckoutPreview?: boolean;
};

export function usePosCheckoutPane({
  navigation,
  currentBranchId,
  cashSessionReady,
  stockSalePolicy,
  cart,
  cartEvaluation,
  promoUiStatus = null,
  saleItems,
  discretionaryDiscountTotal = 0,
  appliedPromoCodes = [],
  selectedCashierPromotionId = null,
  clearCart,
  userId,
  canOpenCashManagementFromPos,
  mercadopagoQrMethodAllowed,
  customerMethodAllowed,
  checkMethodAllowed,
  allowFiscalSaleMode,
  defaultCheckoutFiscalMode,
  hospitalityEnabled,
  hasKitchenCapability,
  hasTablesCapability,
  activeDraftId,
  clearPersistenceAfterSale,
  reloadCatalog,
  setMobileCartSheetVisible,
  initialDiningTableId,
  tableOpenKey = null,
  tablesideCheckoutPreview = false,
}: Params) {
  const diningTableIdRef = useRef<string | null>(null);
  const openCheckoutRef = useRef<() => void | Promise<void>>(() => {});
  const openCheckoutLazy = useCallback(() => openCheckoutRef.current(), []);

  const checkoutStore = usePosCheckoutStore(
    useShallow((s) => ({
      checkoutVisible: s.checkoutVisible,
      checkoutLoading: s.checkoutLoading,
      paymentDrafts: s.paymentDrafts,
      saleFiscalMode: s.saleFiscalMode,
      fiscalBuyerDraft: s.fiscalBuyerDraft,
      paymentMethods: s.paymentMethods,
      customerOptions: s.customerOptions,
      customersTotal: s.customersTotal,
      customerPageIndex: s.customerPageIndex,
      customerPageSize: s.customerPageSize,
      stockWarnings: s.stockWarnings,
      stockWarningDetails: s.stockWarningDetails,
      stockWarningsLoading: s.stockWarningsLoading,
      stockCanProceed: s.stockCanProceed,
      showQrModal: s.showQrModal,
      lastMpQrSaleId: s.lastMpQrSaleId,
    }))
  );

  const cashSession = usePosCashSessionStore(
    useShallow((s) => ({
      activeCashSessionId: s.activeCashSessionId,
      cashSessionLoading: s.cashSessionLoading,
      cashSessionHydratedForBranchId: s.cashSessionHydratedForBranchId,
    }))
  );

  const setCheckoutVisible = usePosCheckoutStore((s) => s.setCheckoutVisible);
  const setSaleFiscalMode = usePosCheckoutStore((s) => s.setSaleFiscalMode);
  const setFiscalBuyerDraft = usePosCheckoutStore((s) => s.setFiscalBuyerDraft);
  const resetCheckout = usePosCheckoutStore((s) => s.resetCheckout);
  const closeQrModal = usePosCheckoutStore((s) => s.closeQrModal);

  const {
    diningTablePickerVisible,
    setDiningTablePickerVisible,
    diningTableOptions,
    diningTablePickerLoading,
    selectedDiningTable,
    selectedDiningTableId,
    beginCheckout,
    confirmDiningTableAndCheckout,
    clearDiningTableSelection,
    openChangeDiningTablePicker,
  } = usePosDiningCheckout({
    currentBranchId,
    hasTablesCapability,
    openCheckout: openCheckoutLazy,
    diningTableIdRef,
    initialDiningTableId,
  });

  const activeDiningTableId = hasTablesCapability
    ? (initialDiningTableId ?? selectedDiningTableId)
    : null;

  const {
    getTableSessionId,
    closeActiveTableSession,
    allowEmptyCartPersist,
    sessionCovers,
    sessionOpenedAt,
  } = usePosTableSession({
    currentBranchId: hasTablesCapability ? currentBranchId : null,
    diningTableId: activeDiningTableId,
    openKey: hasTablesCapability ? tableOpenKey : null,
    checkoutPreview: hasTablesCapability ? tablesideCheckoutPreview : false,
  });

  const getDiningTableName = useCallback((): string | null => {
    const fromSelected = selectedDiningTable?.name?.trim();
    if (fromSelected) return fromSelected;
    const id = diningTableIdRef.current;
    if (!id) return null;
    return diningTableOptions.find((t) => t.id === id)?.name?.trim() ?? null;
  }, [diningTableOptions, selectedDiningTable]);

  const resetDiningTableAfterSale = useCallback(() => {
    clearDiningTableSelection();
    navigation.setParams?.({
      diningTableId: undefined,
      tableOpenKey: undefined,
      tablesideCheckoutPreview: undefined,
    } as Record<string, unknown>);
  }, [clearDiningTableSelection, navigation]);

  /** Vaciar carrito en cuenta de mesa: permite persistir [] (si no, el debounce protege contra wipe accidental). */
  const clearCartIncludingTableSession = useCallback(() => {
    allowEmptyCartPersist();
    clearCart();
  }, [allowEmptyCartPersist, clearCart]);

  const hasSplitBillCapability = useHasBusinessCapability('split_bill');
  const replaceCart = usePosStore((s) => s.replaceCart);
  const splitRemainingRef = useRef<CartItem[] | null>(null);
  const [splitBillModalVisible, setSplitBillModalVisible] = useState(false);

  const canSplitBill = Boolean(
    hasSplitBillCapability &&
      (activeDiningTableId || diningTableIdRef.current) &&
      cart.length >= 2,
  );

  const openSplitBillModal = useCallback(() => {
    if (!canSplitBill) return;
    setSplitBillModalVisible(true);
  }, [canSplitBill]);

  const confirmSplitBill = useCallback(
    (selectedLineIds: string[]) => {
      const selectedSet = new Set(selectedLineIds);
      const selected = cart.filter((l) => selectedSet.has(l.lineId));
      const remaining = cart.filter((l) => !selectedSet.has(l.lineId));
      if (selected.length === 0 || remaining.length === 0) return;
      splitRemainingRef.current = remaining;
      const sessionId = getTableSessionId();
      if (sessionId) {
        void upsertTableSessionCart({ sessionId, cartJson: remaining }).catch(captureCatch('pos', 'split-bill-cart-save'));
      }
      replaceCart(selected);
      setSplitBillModalVisible(false);
    },
    [cart, getTableSessionId, replaceCart],
  );

  const shouldKeepTableSessionOpen = useCallback(
    () => Boolean(splitRemainingRef.current && splitRemainingRef.current.length > 0),
    [],
  );

  const onAfterPartialTableSale = useCallback(async () => {
    const remaining = splitRemainingRef.current ?? [];
    splitRemainingRef.current = null;
    const sessionId = getTableSessionId();
    if (sessionId) {
      await upsertTableSessionCart({ sessionId, cartJson: remaining }).catch(captureCatch('pos', 'partial-sale-table-cart-save'));
    }
    replaceCart(remaining);
  }, [getTableSessionId, replaceCart]);


  useEffect(() => {
    if (!allowFiscalSaleMode && checkoutStore.saleFiscalMode === 'fiscal') {
      setSaleFiscalMode('non_fiscal');
    }
  }, [allowFiscalSaleMode, checkoutStore.saleFiscalMode, setSaleFiscalMode]);

  const loadMoreCustomers = useCallback(async (pageIndex: number, loadAll = false) => {
    const { setCustomerOptions, setCustomerPageIndex } = usePosCheckoutStore.getState();
    setCustomerPageIndex(pageIndex);
    const offset = pageIndex * checkoutStore.customerPageSize;
    const customers = await listPosCheckoutCustomers({
      limit: loadAll ? undefined : checkoutStore.customerPageSize,
      offset: loadAll ? 0 : offset,
    });
    setCustomerOptions(customers);
  }, [checkoutStore.customerPageSize]);

  const {
    isSaving,
    open: openCheckout,
    complete: completeSale,
    sendCourseToKitchen,
    canSendCourseToKitchen,
    updateDraft,
    removeDraft,
    addSplitPayment,
    handleRequestStockCorrection,
    handleOpenCashManagementFromCheckout,
    clearPendingSaleId,
    ConfirmModal,
    CheckoutOverlayModal,
    SupervisorPinModal,
  } = usePosCheckoutTransaction({
    navigation,
    currentBranchId,
    saleItems,
    cart,
    cartEvaluation: cartEvaluation ?? null,
    promoUiStatus,
    discretionaryDiscountTotal,
    appliedPromoCodes,
    selectedCashierPromotionId,
    canOpenCashManagement: canOpenCashManagementFromPos,
    mercadopagoQrMethodAllowed,
    customerMethodAllowed,
    checkMethodAllowed,
    allowFiscalSaleMode,
    defaultSaleFiscalMode: defaultCheckoutFiscalMode,
    onAfterSaleRecorded: async () => {
      await clearPersistenceAfterSale();
      await reloadCatalog();
    },
    allowsHospitality: hospitalityEnabled,
    saleDraftId: activeDraftId,
    kitchenTicketsEnabled: hasKitchenCapability,
    getDiningTableId: () => diningTableIdRef.current,
    getDiningTableName,
    getTableSessionId,
    clearCart,
    clearPersistenceAfterSale,
    onCloseTableSession: closeActiveTableSession,
    shouldKeepTableSessionOpen,
    onAfterPartialTableSale,
    onResetDiningTableAfterSale: resetDiningTableAfterSale,
  });

  useEffect(() => {
    openCheckoutRef.current = openCheckout;
  }, [openCheckout]);

  const handlePosOpenCheckout = useCallback(() => {
    setMobileCartSheetVisible(false);
    void beginCheckout();
  }, [beginCheckout, setMobileCartSheetVisible]);

  const {
    mpQrSession,
    finalizeMpQrPosSession,
    handleConfigureMercadoPagoFromQr,
    handleChangePaymentMethodFromQr,
    handleOpenMpQrSaleInVentas,
    handleVoidMpQrSale,
  } = usePosMpQrActions({
    navigation,
    currentBranchId,
    lastMpQrSaleId: checkoutStore.lastMpQrSaleId,
    userId,
    clearCart,
    resetCheckout,
    closeQrModal,
    setCheckoutVisible,
    clearPersistenceAfterSale,
    clearPendingSaleId,
  });

  const cashSessionOpenForCheckout = !cashSessionReady ? null : Boolean(cashSession.activeCashSessionId);

  usePosStockWarningsForCheckout(currentBranchId, saleItems, stockSalePolicy);
  const loyaltyRedeemPoints = usePosLoyaltyStore((s) => s.redeemPoints);
  const loyaltyProgram = usePosLoyaltyStore((s) => s.program);
  const loyaltyExtraDiscount = redeemPointsToArs(loyaltyRedeemPoints, loyaltyProgram?.redeemArsPerPoint ?? 0);

  const tipIntent = usePosCheckoutStore((s) => s.tipIntent);
  const tipUiEnabled = usePosCheckoutStore((s) => s.tipUiEnabled);
  const setTipIntent = usePosCheckoutStore((s) => s.setTipIntent);
  const setTipUiEnabled = usePosCheckoutStore((s) => s.setTipUiEnabled);
  const setPaymentDrafts = usePosCheckoutStore((s) => s.setPaymentDrafts);
  const staffTipsEnabled = usePosStaffTipsEnabled();

  useEffect(() => {
    if (staffTipsEnabled) {
      setTipUiEnabled(true);
      if (!usePosCheckoutStore.getState().tipIntent) {
        setTipIntent({ amount: 0, percent: null, source: 'staff' });
      }
    } else {
      setTipUiEnabled(false);
      setTipIntent({ amount: 0, percent: null, source: 'staff' });
    }
  }, [staffTipsEnabled, setTipIntent, setTipUiEnabled]);

  const showTipBlock = staffTipsEnabled && tipUiEnabled;
  const tipAmount = resolvePosStaffTipAmount({
    tipIntentAmount: Number(tipIntent?.amount ?? 0),
    tipUiEnabled,
    staffTipsEnabled,
  });
  const checkoutMoney = derivePosCheckoutMoney(
    cart,
    checkoutStore.paymentDrafts,
    cartEvaluation,
    { extraDiscountTotal: loyaltyExtraDiscount, tipAmount },
  );
  const {
    subtotal,
    discountTotal,
    total,
    totalIngresado,
    vuelto,
    saldoPendiente,
    merchandiseTotal,
  } = checkoutMoney;
  const merchandiseTotals = checkoutMoney;

  // Propina % + un solo medio: si cambia el subtotal (promo, drift) mantenemos
  // el monto al día antes de que el cajero confirme.
  useEffect(() => {
    if (!checkoutStore.checkoutVisible) return;
    if (showTipBlock && tipIntent?.percent != null && tipIntent.percent > 0) {
      const nextAmount = tipAmountFromPercent(merchandiseTotals.total, tipIntent.percent);
      if (Math.abs(nextAmount - (tipIntent.amount ?? 0)) > 0.01) {
        setTipIntent({ ...tipIntent, amount: nextAmount, source: tipIntent.source ?? 'staff' });
      }
    }
    const drafts = usePosCheckoutStore.getState().paymentDrafts;
    if (drafts.length !== 1) return;
    const draft = drafts[0];
    if (draft.methodId === 'cash' || draft.methodId === 'mercadopago_qr') return;
    const charged = formatEditableMoneyAmount(total);
    if (draft.amount !== charged) {
      setPaymentDrafts([{ ...draft, amount: charged }]);
    }
  }, [
    checkoutStore.checkoutVisible,
    merchandiseTotals.total,
    setPaymentDrafts,
    setTipIntent,
    tipIntent,
    showTipBlock,
    total,
  ]);

  const selectTipIntent = useCallback(
    (next: PosTipIntent | null) => {
      const merchandise = merchandiseTotals.total;
      let resolved: PosTipIntent | null = next;
      if (next && next.percent != null && next.percent > 0) {
        resolved = {
          ...next,
          amount: tipAmountFromPercent(merchandise, next.percent),
          source: 'staff',
        };
      } else if (next) {
        resolved = { ...next, source: 'staff' };
      }
      setTipIntent(resolved);
      const nextTip = Math.max(0, Number(resolved?.amount ?? 0));
      const charged = merchandise + nextTip;
      setPaymentDrafts((current) => {
        if (current.length !== 1) return current;
        return [{ ...current[0], amount: formatEditableMoneyAmount(charged) }];
      });
      const sessionId = getTableSessionId();
      if (sessionId) {
        void upsertTableSessionTipIntent({
          sessionId,
          tipIntent: resolved,
        }).catch(captureCatch('pos', 'table-session-tip-intent'));
      }
    },
    [getTableSessionId, merchandiseTotals.total, setPaymentDrafts, setTipIntent],
  );

  const canPrintPreCuenta = cart.length > 0;
  const printTicketIsPreCuenta = Boolean(activeDiningTableId);
  const printTicketCounterSummary = hasKitchenCapability && !printTicketIsPreCuenta;

  const buildPreCuentaArgs = useCallback(() => {
    const tableId = activeDiningTableId;
    const lines = mapCartLikeToPreCuentaLines(
      cart.map((line) => ({
        name: line.name ?? '',
        quantity: line.quantity,
        price: line.price,
        optionsLabel: formatOptionsLabel(line.menuOptions) || null,
        lineNote: line.lineNote?.trim() || null,
      }))
    );
    return {
      tableId,
      tableName: tableId ? (selectedDiningTable?.name ?? 'Mesa') : 'Mostrador',
      covers: tableId ? sessionCovers : null,
      lines,
      subtotal,
      discountTotal,
      tipAmount: tipAmount > 0 ? tipAmount : undefined,
      total,
      openedAt: tableId ? sessionOpenedAt : null,
      context: (tableId ? 'mesa' : 'counter') as 'mesa' | 'counter',
    };
  }, [
    cart,
    discountTotal,
    activeDiningTableId,
    selectedDiningTable,
    sessionCovers,
    sessionOpenedAt,
    subtotal,
    tipAmount,
    total,
  ]);

  const printPreCuenta = useCallback(async () => {
    if (cart.length === 0) {
      showInfo('Sin cuenta', 'Agregá productos al carrito.');
      return;
    }
    if (!currentBranchId) return;
    const args = buildPreCuentaArgs();
    await printPreCuentaDocumentSafe({
      branchId: currentBranchId,
      tableName: args.tableName,
      covers: args.covers,
      lines: args.lines,
      subtotal: args.subtotal,
      discountTotal: args.discountTotal,
      tipAmount: args.tipAmount,
      total: args.total,
      openedAt: args.openedAt,
      context: args.context,
      onError: showError,
    });
  }, [buildPreCuentaArgs, cart.length, currentBranchId]);

  const sharePreCuenta = useCallback(async () => {
    if (cart.length === 0) {
      showInfo('Sin cuenta', 'Agregá productos al carrito.');
      return;
    }
    const args = buildPreCuentaArgs();
    await sharePreCuentaDocumentSafe({
      tableName: args.tableName,
      covers: args.covers,
      lines: args.lines,
      subtotal: args.subtotal,
      discountTotal: args.discountTotal,
      tipAmount: args.tipAmount,
      total: args.total,
      openedAt: args.openedAt,
      context: args.context,
      onError: showError,
      onCopied: () => showSuccess('Cuenta', 'Copiada'),
    });
  }, [buildPreCuentaArgs, cart.length]);

  const stockWarningByProductId = useMemo(
    () => mapStockWarningsByProductId(checkoutStore.stockWarningDetails),
    [checkoutStore.stockWarningDetails]
  );
  const hasBlockingStockIssue = checkoutStore.stockWarnings.length > 0 && !checkoutStore.stockCanProceed;
  const hasExpiredStock = checkoutStore.stockWarningDetails.some((d) => d.warningType === 'expired_stock');
  const customerEligibleCustomers = useMemo(
    () => checkoutStore.customerOptions.filter((customer) => !customer.blocked),
    [checkoutStore.customerOptions]
  );

  return {
    stockSalePolicy,
    cashSessionReady,
    activeCashSessionId: cashSession.activeCashSessionId,
    cashSessionLoading: cashSession.cashSessionLoading,
    handleOpenCashManagementFromCheckout,
    checkoutVisible: checkoutStore.checkoutVisible,
    checkoutLoading: checkoutStore.checkoutLoading,
    paymentDrafts: checkoutStore.paymentDrafts,
    saleFiscalMode: checkoutStore.saleFiscalMode,
    setSaleFiscalMode,
    fiscalBuyerDraft: checkoutStore.fiscalBuyerDraft,
    setFiscalBuyerDraft,
    paymentMethods: checkoutStore.paymentMethods,
    customerOptions: checkoutStore.customerOptions,
    customersTotal: checkoutStore.customersTotal,
    customerPageIndex: checkoutStore.customerPageIndex,
    customerPageSize: checkoutStore.customerPageSize,
    stockWarningsLoading: checkoutStore.stockWarningsLoading,
    stockCanProceed: checkoutStore.stockCanProceed,
    stockWarningDetails: checkoutStore.stockWarningDetails,
    stockWarningByProductId,
    hasBlockingStockIssue,
    hasExpiredStock,
    customerEligibleCustomers,
    subtotal,
    discountTotal,
    total,
    merchandiseTotal,
    tipAmount,
    tipIntent,
    tipUiEnabled: showTipBlock,
    selectTipIntent: showTipBlock ? selectTipIntent : undefined,
    totalIngresado,
    vuelto,
    saldoPendiente,
    isSaving,
    beginCheckout,
    handlePosOpenCheckout,
    completeSale,
    sendCourseToKitchen,
    canSendCourseToKitchen,
    printPreCuenta,
    sharePreCuenta,
    canPrintPreCuenta,
    printTicketIsPreCuenta,
    printTicketCounterSummary,
    canSplitBill,
    openSplitBillModal,
    splitBillModalVisible,
    setSplitBillModalVisible,
    confirmSplitBill,
    clearCartIncludingTableSession,
    updateDraft,
    removeDraft,
    addSplitPayment,
    handleRequestStockCorrection,
    loadMoreCustomers,
    resetCheckout,
    cashSessionOpenForCheckout,
    diningTablePickerVisible,
    setDiningTablePickerVisible,
    diningTableOptions,
    diningTablePickerLoading,
    selectedDiningTable,
    confirmDiningTableAndCheckout,
    openChangeDiningTablePicker,
    hasTablesCapability,
    showQrModal: checkoutStore.showQrModal,
    lastMpQrSaleId: checkoutStore.lastMpQrSaleId,
    mpQrSession,
    finalizeMpQrPosSession,
    handleChangePaymentMethodFromQr,
    handleOpenMpQrSaleInVentas,
    handleVoidMpQrSale,
    handleConfigureMercadoPagoFromQr,
    clearPendingSaleId,
    ConfirmModal,
    CheckoutOverlayModal,
    SupervisorPinModal,
  };
}
